/**
 * Can this FORM be chased in this variant right now?
 *
 * Shared by every surface that renders form-scoped event gaps (the Calendar
 * gap strips, the Profile radar strips, the post-event Quick-update card) so
 * they agree about which costume/regional tiles exist at all. Released on the
 * device clock (event costumes ship in the bundle ahead of their 10:00-local
 * flip), and a shiny tile needs the form to have shiny art at all. Unknown
 * formIds (a link from a newer catalogue than this build) fail closed to
 * hidden.
 */
import { FORM_GROUPS_BY_FORM_ID } from "@/lib/generated/pokemonForms.generated";
import { isFormShinyReleased, isFormVariantReleased } from "@/lib/types/forms";
import type { RaidDexVariant } from "@/lib/raidDexCrossRef";
import { isMegaFormId, megaFormForFormId } from "@/lib/megaDex";
import { DYNAMAX_FORM_ID, speciesHasDynamax } from "@/lib/dynamaxDex";
import { isVariantAvailable } from "@/lib/variantAvailability";

export function isEventFormAvailable(variant: RaidDexVariant, formId: string, speciesId?: number): boolean {
  // A Dynamax-scoped copy (Shiny Dynamax, 100% Dynamax …, 2026-09-04) is
  // available when the species can Dynamax at all (curated roster) and the
  // base variant is out for it, exactly like the Mega copies below.
  if (formId === DYNAMAX_FORM_ID) {
    if (speciesId == null || !speciesHasDynamax(speciesId)) return false;
    return variant === "dynamax" || isVariantAvailable(variant, speciesId);
  }
  // A Mega-scoped copy (Shiny Mega, 100% Mega …, 2026-09-03) is available
  // when the species has that Mega form and the base variant is out for the
  // species: a Mega changes the look, never which variants can exist.
  if (isMegaFormId(formId)) {
    if (speciesId == null) return false;
    if (!megaFormForFormId(speciesId, formId)) return false;
    return variant === "mega" || isVariantAvailable(variant, speciesId);
  }
  const form = FORM_GROUPS_BY_FORM_ID.get(formId)?.forms.find((f) => f.formId === formId);
  if (!form) return false;
  if (!isFormVariantReleased(form)) return false;
  // A form carries a variant only if its species does (2026-09-07): an
  // untradable species has no Lucky costume, a species whose shiny is out
  // nowhere has no shiny regional. Main is the form itself, always.
  if (variant !== "pokemon" && speciesId != null && !isVariantAvailable(variant, speciesId)) return false;
  // Shiny needs the form's OWN shiny to be released, not just shiny art in
  // the mirror: Armored Mewtwo has the icon and no shiny in GO.
  if (variant === "shiny" && !isFormShinyReleased(form)) return false;
  return true;
}
